import { useContext, useEffect, useState } from 'react';
import { CompareSettings } from './CompareSettings';
import Container from '@/components/layout/Container';
import { findBiggestDiscount } from '@/utils/discounts';
import Link from 'next/link';
import Price from '@/components/price/Price';
import Image from 'next/image';
import { useCampaign } from '@/app/CampaignContext';
import { useModal } from '@/components/modal/ModalProvider';

export default function ImageCards({
  block,
  entryUid,
  products,
  onDesktop,
  position
}) {
  const campaign = useCampaign();
  const { image_cards } = block;
  const compareSettings = useContext(CompareSettings);
  const { pricing_details, discount_tag } = campaign;
  const [visibleProducts, setVisibleProducts] = useState([]);
  const { showPriceDetails } = useModal();

  useEffect(() => {
    const tempArr = [];
    if (onDesktop) {
      compareSettings.desktopSelectors.forEach((selected) => {
        const matchingProduct = products[selected.index];
        tempArr.push({
          selectorProduct: matchingProduct,
          index: selected.index
        });
      });
    } else {
      compareSettings.mobileSelectors.forEach((selected) => {
        const matchingProduct = products[selected.index];
        tempArr.push({
          selectorProduct: matchingProduct,
          index: selected.index
        });
      });
    }

    setVisibleProducts(tempArr);
  }, [compareSettings, onDesktop, products]);

  const [affirmProductInfo, setAffirmProductInfo] = useState([]);

  useEffect(() => {
    if (visibleProducts.length > 0) {
      const tempAffirmProductInfo = visibleProducts.map((product) => {
        const selectedVariant =
          product?.selectorProduct?.reference[0].shopifyProduct[0].variants[0];
        return {
          price:
            (parseInt(selectedVariant.compareAtPrice.amount, 10) <
            parseInt(selectedVariant.price.amount, 10)
              ? parseInt(selectedVariant.compareAtPrice.amount, 10)
              : parseInt(selectedVariant.price.amount, 10)) * 100
        };
      });
      setAffirmProductInfo(tempAffirmProductInfo);
    }
  }, [visibleProducts]);

  const [developDomain, setDevelopDomain] = useState(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      setDevelopDomain(
        hostname.indexOf('beta') !== -1 ||
          hostname.indexOf('alpha') !== -1 ||
          hostname.indexOf('localhost') !== -1 ||
          hostname.indexOf('vercel') !== -1
      );
    }
  }, []);

  useEffect(() => {
    if (developDomain !== null && typeof window['affirm'] === 'undefined') {
      const affirmApiKey = developDomain
        ? 'FWUPRAEPEJ8CDZUV'
        : '2LSY5K7PY0C2IZQ7';
      const affirmScript = developDomain
        ? 'https://cdn1-sandbox.affirm.com/js/v2/affirm.js'
        : 'https://cdn1.affirm.com/js/v2/affirm.js';

      var _affirm_config = {
        public_api_key: `${affirmApiKey}`,
        script: `${affirmScript}`,
        locale: 'en_US',
        country_code: 'USA'
      };

      (function (m, g, n, d, a, e, h, c) {
        var b = m[n] || {},
          k = document.createElement(e),
          p = document.getElementsByTagName(e)[0],
          l = function (a, b, c) {
            return function () {
              a[b]._.push([c, arguments]);
            };
          };
        b[d] = l(b, d, 'set');
        var f = b[d];
        b[a] = {};
        b[a]._ = [];
        f._ = [];
        b._ = [];
        b[a][h] = l(b, a, h);
        b[c] = function () {
          b._.push([h, arguments]);
        };
        a = 0;
        for (
          c =
            'set add save post open empty reset on off trigger ready setProduct'.split(
              ' '
            );
          a < c.length;
          a++
        )
          f[c[a]] = l(b, d, c[a]);
        a = 0;
        for (c = ['get', 'token', 'url', 'items']; a < c.length; a++)
          f[c[a]] = function () {};
        k.async = !0;
        k.src = g[e];
        p.parentNode.insertBefore(k, p);
        delete g[e];
        f(g);
        m[n] = b;
      })(
        window,
        _affirm_config,
        'affirm',
        'checkout',
        'ui',
        'script',
        'ready',
        'jsReady'
      );
    }
  }, [developDomain]);

  useEffect(() => {
    if (typeof window['affirm'] !== 'undefined') {
      window.affirm.ui.ready(function () {
        window.affirm.ui.refresh();
      });
    }
  }, [affirmProductInfo]);

  const renderPriceDetails = (product) =>
    pricing_details && (
      <div
        className={`cursor-pointer pb-1 text-center underline ${onDesktop ? 'body-s' : 'body-xs'}`}
        onClick={() => {
          showPriceDetails(`image-cards-${product}`, null, pricing_details);
        }}
      >
        Price details
      </div>
    );

  return (
    <section
      id={entryUid + '_' + position}
      data-name="compare-products-cards"
      className="min-h-[510px] pt-4 pb-6 lg:min-h-[680px] lg:pt-6"
    >
      <Container fullPage={false}>
        <div className="grid grid-cols-2 gap-x-[16px] sm:gap-x-[24px] lg:grid-cols-3 lg:gap-x-[32px]">
          {visibleProducts.map((product, index) => {
            let discountMsg = '';
            if (product?.selectorProduct) {
              discountMsg = findBiggestDiscount(
                product?.selectorProduct?.reference[0]?.shopifyProduct[0]
                  ?.variants,
                campaign
              );
            }
            return (
              <div className={`relative flex w-full flex-col`} key={index}>
                <div className="border-dawn-grey-60 relative mx-auto min-h-[300px] min-w-full flex-1 border bg-white pb-3 lg:min-h-[400px] lg:pb-4">
                  <div className="relative">
                    {discountMsg !== '' && (
                      <div className="absolute top-0 right-0 inline-block text-center uppercase">
                        <span
                          className={`relative z-1 px-3 py-2 text-[${discount_tag.text_color_picker}] badge-text-s lg:badge-text-r bg-[${discount_tag.background_color_picker}]`}
                        >
                          <span>{discountMsg}</span>
                        </span>
                      </div>
                    )}
                    {product?.selectorProduct?.reference[0].url && (
                      <Link
                        href={product?.selectorProduct?.reference[0]?.url}
                        prefetch={false}
                      >
                        <Image
                          src={image_cards?.products[product?.index]?.image.url}
                          data-src={
                            image_cards?.products[product?.index]?.image.url
                          }
                          alt={image_cards?.products[
                            product?.index
                          ]?.image.title.replace(/\.[^/.]+$/, '')}
                          className="lazyload block w-full cursor-pointer"
                          width="379"
                          height="249"
                          sizes="(min-width: 1536px) 379px, (min-width: 1024px) 335px,(min-width: 1024px) 270px, 270px"
                        />
                      </Link>
                    )}
                  </div>
                  <h3
                    data-cy={`compare-image-title-${index}`}
                    className={`${onDesktop ? 'h4' : 'h6'} mb-2 block px-4 pt-3 text-center lg:mb-3 lg:pt-4`}
                  >
                    {product?.selectorProduct?.reference[0].title.replace(
                      'Mattress',
                      ''
                    )}
                  </h3>

                  <div className="object-bottom">
                    <div className="w-full">
                      <div
                        className={`flex w-full justify-center pb-1 sm:pb-0 lg:mb-1`}
                      >
                        <span className="price-s-label lg:price-l-label self-end pr-2">
                          From
                        </span>
                        <Price
                          price={
                            product?.selectorProduct?.reference[0]
                              .shopifyProduct[0]?.variants[0].price.amount
                          }
                          compareAtPrice={
                            product?.selectorProduct?.reference[0]
                              .shopifyProduct[0]?.variants[0].compareAtPrice
                              .amount
                          }
                          priceClassOverride={`price-s lg:price-l text-[#CC4D0F]!`}
                          crossedPriceClassOverride={`price-s lg:price-l-crossed text-[#757575]`}
                          variant={
                            product?.selectorProduct?.reference[0]
                              .shopifyProduct[0]?.handle
                          }
                        />
                      </div>

                      {renderPriceDetails(
                        product?.selectorProduct?.reference[0].shopifyProduct[0]
                          ?.handle
                      )}
                      <div className="block w-full justify-center">
                        <div
                          id={`finance-msg-compare-${index}`}
                          className="affirm-as-low-as body-s finance-msg-compare min-h-15 text-center text-black min-[433px]:min-h-10"
                          data-page-type="cart"
                          data-affirm-color="black"
                          data-amount={
                            parseInt(
                              product?.selectorProduct?.reference[0]
                                .shopifyProduct[0]?.variants[0].price.amount,
                              10
                            ) * 100
                          }
                          aria-label="See if you qualify (opens in modal)"
                        ></div>
                      </div>
                    </div>
                    {product?.selectorProduct?.reference[0].url && (
                      <div className="main-shop-button flex flex-row items-center justify-center px-3 lg:px-4">
                        <Link
                          href={product?.selectorProduct?.reference[0].url}
                          prefetch={false}
                          title="Shop Now"
                          className="btn-primary btn-s font-lato mx-auto mt-[24px] w-full py-[9px] text-center font-bold lg:py-3 lg:text-base"
                        >
                          Shop Now
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {visibleProducts.map((product, index) => {
            return (
              <div
                key={index}
                className={`bg-[${image_cards?.description_background_color}] font-lustria mt-4 flex items-center border-none! px-3 py-5 text-center lg:mt-6 lg:p-6 ${onDesktop ? 'h5' : 'h7'}`}
              >
                <div className="">
                  {image_cards?.products[product?.index]?.description}
                </div>
              </div>
            );
          })}
        </div>
      </Container>
    </section>
  );
}
